import { useDisplaySettings } from '@src/providers/AppConfigProvider';
import { useLpSettlementBatchQuery } from '@src/providers/AppStateProvider/streamingDataSlice';
import { useCounterpartiesQueryData } from '@src/providers/Counterparties/useCounterpartiesQueryData';
import {
  Alert,
  AlertVariants,
  Box,
  Dialog,
  DialogContent,
  DialogFooter,
  EMPTY_ARRAY,
  formattedDate,
  FormControlSizes,
  Label,
  LPSettlementBatchStatusEnum,
  MixpanelEvent,
  MixpanelEventSource,
  SearchSelect,
  Text,
  type useDisclosureWithContext,
  useMarketAccountsContext,
  useMixpanel,
  VStack,
} from '@talos/kyoko';
import { uniqBy } from 'lodash-es';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { LPSettlementBatchMessageData } from '../generated/lpSettlementTypes';
import { useAddToBatchMutation, usePreviewSettlementBatchQuery } from '../useLpSettlementApi';
import { buildTradesAddedToBatchEventProperties } from '../utils/lpSettlementMixpanelUtils';
import { toSettleBatchRequestEvent } from '../utils/toSettleBatchRequestEvent';
import { ManualBatchPreviewSummary } from './ManualBatchPreviewSummary';
import type { AddToBatchDisclosureContext } from './UnsettledTransactionsBlotter';

interface AddToBatchDialogProps extends ReturnType<typeof useDisclosureWithContext<AddToBatchDisclosureContext>> {
  onSubmit: (result: { batchID: string; transactionCount: number }) => void;
}

interface OpenBatchOption {
  batch: LPSettlementBatchMessageData;
  label: string;
  description: string;
}

export const AddToBatchDialog = ({ onSubmit, ...disclosure }: AddToBatchDialogProps) => {
  return (
    <Dialog
      {...disclosure}
      closeOnEscape
      title="Add to Existing Batch"
      showClose
      width={520}
      controlledContent
      suppressAutoEnterForm
      data-testid="add-to-batch-dialog"
    >
      {disclosure.context && (
        <AddToBatchDialogContent
          key={disclosure.openId}
          disclosure={disclosure}
          context={disclosure.context}
          onSubmit={onSubmit}
        />
      )}
    </Dialog>
  );
};

interface AddToBatchDialogContentProps {
  context: AddToBatchDisclosureContext;
  disclosure: ReturnType<typeof useDisclosureWithContext<AddToBatchDisclosureContext>>;
  onSubmit: (result: { batchID: string; transactionCount: number }) => void;
}

function AddToBatchDialogContent({ context, disclosure, onSubmit }: AddToBatchDialogContentProps) {
  const { marketAccountsByName } = useMarketAccountsContext();
  const { counterpartiesByName } = useCounterpartiesQueryData();
  const { homeCurrency } = useDisplaySettings();
  const mixpanel = useMixpanel(MixpanelEventSource.PortfolioLpSettlement);
  const [selectedBatchID, setSelectedBatchID] = useState<string | undefined>(undefined);
  const [openBatchesRetryKey, setOpenBatchesRetryKey] = useState(0);

  const selectedTrades = uniqBy(context?.selectedTrades ?? EMPTY_ARRAY, item => item.EventID);
  const marketAccount = selectedTrades[0]?.MarketAccount;

  const summaryData = useMemo(() => {
    if (selectedTrades.length === 0) {
      return {
        counterparty: '',
        marketAccount: '',
        totalTransactions: '',
      };
    }

    return {
      counterparty: selectedTrades[0].Counterparty ?? '',
      marketAccount: selectedTrades[0].MarketAccount,
      totalTransactions: selectedTrades.length.toString(),
    };
  }, [selectedTrades]);

  const {
    isLoading: isPreviewLoading,
    data: batchData,
    isError: isPreviewError,
    refetch: retryPreviewSettlement,
    isSuccess: isPreviewSuccess,
  } = usePreviewSettlementBatchQuery({ selectedTrades, homeCurrency }, { skip: selectedTrades.length === 0 });
  const [addToBatch, { isLoading: busy }] = useAddToBatchMutation();

  const settleBatchInstructions = useMemo(() => batchData?.[0]?.SettleBatchInstructions ?? EMPTY_ARRAY, [batchData]);

  const {
    data: openBatchesById,
    isLoading: isBatchesLoading,
    isError: isBatchesError,
    isSuccess: isBatchesSuccess,
  } = useLpSettlementBatchQuery(
    {
      MarketAccount: marketAccount,
      Statuses: [LPSettlementBatchStatusEnum.Created],
      // A failed subscribe stays cached for keepUnusedDataFor. A new tag is a new cache key, so Retry
      // starts a fresh stream without waiting that out or leaving the page.
      tag: `add-to-batch-dialog-${disclosure.openId}-${openBatchesRetryKey}`,
    },
    { skip: !marketAccount }
  );

  const retryOpenBatches = useCallback(() => {
    setOpenBatchesRetryKey(key => key + 1);
  }, []);

  const batchOptions = useMemo<OpenBatchOption[]>(() => {
    if (!openBatchesById || !marketAccount) {
      return [];
    }

    return Array.from(openBatchesById.values())
      .filter(batch => batch.Status === LPSettlementBatchStatusEnum.Created && batch.MarketAccount === marketAccount)
      .toSorted((a, b) => {
        const deadlineCompare = (a.SettleDeadline ?? '').localeCompare(b.SettleDeadline ?? '');
        if (deadlineCompare !== 0) {
          return deadlineCompare;
        }
        return a.BatchID.localeCompare(b.BatchID);
      })
      .map(batch => {
        const marketAccountDisplayName =
          marketAccountsByName.get(batch.MarketAccount)?.DisplayName ?? batch.MarketAccount;
        return {
          batch,
          label: `ID: ${batch.BatchID}`,
          description: `${marketAccountDisplayName} • Due Date: ${formattedDate(batch.SettleDeadline)}`,
        };
      });
  }, [marketAccount, marketAccountsByName, openBatchesById]);

  const resolvedSelectedBatchID = useMemo(() => {
    if (batchOptions.length === 1) {
      return batchOptions[0].batch.BatchID;
    }
    if (batchOptions.some(option => option.batch.BatchID === selectedBatchID)) {
      return selectedBatchID;
    }
    return undefined;
  }, [batchOptions, selectedBatchID]);

  const selectedBatchOption = useMemo(
    () => batchOptions.find(option => option.batch.BatchID === resolvedSelectedBatchID),
    [batchOptions, resolvedSelectedBatchID]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (isPreviewError) {
        retryPreviewSettlement();
      }
      if (isBatchesError) {
        retryOpenBatches();
      }
      if (isPreviewError || isBatchesError) {
        return;
      }
      if (!selectedTrades.length || !selectedBatchOption) {
        return;
      }

      addToBatch({
        batchID: selectedBatchOption.batch.BatchID,
        batchRevision: selectedBatchOption.batch.Revision,
        events: selectedTrades.map(toSettleBatchRequestEvent),
      })
        .unwrap()
        .then(() => {
          mixpanel.track(
            MixpanelEvent.LPSettlementTradesAddedToBatch,
            buildTradesAddedToBatchEventProperties({
              reportCount: selectedTrades.length,
            })
          );
          onSubmit({
            batchID: selectedBatchOption.batch.BatchID,
            transactionCount: selectedTrades.length,
          });
        })
        .catch(() => {
          // Error toast is handled by the mutation's onQueryStarted handler; keep the dialog open.
        });
    },
    [
      addToBatch,
      isBatchesError,
      isPreviewError,
      mixpanel,
      onSubmit,
      retryOpenBatches,
      retryPreviewSettlement,
      selectedBatchOption,
      selectedTrades,
    ]
  );

  const noCompatibleBatches = isBatchesSuccess && batchOptions.length === 0;
  const canRetryLoad = isPreviewError || isBatchesError;

  return (
    <>
      <DialogContent overflow="hidden" style={{ order: 2, height: '100%', overflow: 'auto' }}>
        <form onSubmit={handleSubmit} id="add-to-batch-modal-form">
          <ManualBatchPreviewSummary
            summaryData={summaryData}
            counterpartyDisplayName={
              counterpartiesByName?.get(summaryData.counterparty)?.DisplayName ?? summaryData.counterparty
            }
            marketAccountDisplayName={
              marketAccountsByName.get(summaryData.marketAccount)?.DisplayName ?? summaryData.marketAccount
            }
            homeCurrency={homeCurrency}
            settleBatchInstructions={settleBatchInstructions}
            isPreviewLoading={isPreviewLoading}
            isPreviewError={isPreviewError}
            itemsBlotterTestId="add-to-batch-items-blotter"
            errorTestId="add-to-batch-error"
          />

          <VStack mt="spacing12" gap="spacing8" alignItems="flex-start" w="100%">
            <Label>Settlement Batch</Label>
            <Box w="100%" data-testid="settlement-batch-select">
              <SearchSelect
                options={batchOptions}
                selection={selectedBatchOption}
                onChange={option => setSelectedBatchID(option?.batch.BatchID)}
                equalityChecker={(option, otherOption) => option.batch.BatchID === otherOption?.batch.BatchID}
                getLabel={option => option.label}
                getDescription={option => option.description}
                showDescriptionInButton
                placeholder="Select open batch"
                size={FormControlSizes.Default}
                disabled={noCompatibleBatches || isBatchesLoading || isBatchesError}
                data-testid="settlement-batch-search-select"
              />
            </Box>
            {isBatchesError && (
              <Alert variant={AlertVariants.Negative} dismissable={false} data-testid="open-batches-load-error">
                <Text>Failed to load open batches.</Text>
              </Alert>
            )}
            {noCompatibleBatches && (
              <Alert variant={AlertVariants.Warning} dismissable={false} data-testid="no-open-batches-alert">
                <Text>No open batches available for this market account.</Text>
              </Alert>
            )}
          </VStack>
        </form>
      </DialogContent>
      <Box style={{ order: 3 }}>
        <DialogFooter
          showCancel
          showConfirm
          cancelLabel="Cancel"
          confirmLabel={canRetryLoad ? 'Retry' : 'Add to Batch'}
          confirmDisabled={
            isPreviewLoading ||
            busy ||
            isBatchesLoading ||
            (!canRetryLoad && isPreviewSuccess && settleBatchInstructions.length === 0) ||
            (!canRetryLoad && (selectedBatchOption == null || noCompatibleBatches))
          }
          confirmButtonType="submit"
          confirmButtonForm="add-to-batch-modal-form"
          handleCancel={disclosure.close}
        />
      </Box>
    </>
  );
}
